<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :show-preview-button="false"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <input-rich-text
            name="editor_note"
            label="Editor's note"
            v-model="form.editor_note"
            :language="form.language"
            :error="errors.editor_note ? errors.editor_note[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 dark:text-gray-300 mb-3">The note is for internal use only and won’t be displayed on the site</p>

        <input-text
            label="* Ranking Name"
            v-model="form.name"
            :error="errors.name ? errors.name[0] : null"
        ></input-text>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Internal Links to Categories</p>
            <multi-reference
                create-text="Create new internal link to category and link it to this page"
                link-text="Link existing internal links to categories"
                :highlight="categoryDuplicates"
                :language="languageData"
                :linkable="false"
                :max-items="4"
                v-model="form.internal_links_to_categories"
                :content-type-filters="[
                    'All Category'
                ]"
                model-data="Internal Link"
            />
        </div>

        <Select
            v-if="!data"
            label="Use Filters or Show All?"
            name="show_all"
            v-model.number="form.show_all"
            class="mt-3"
            placeholder="Select a preference"
            :options="[
                { value: 0, name: 'Use Filters' },
                { value: 1, name: 'Show All' },
            ]"
        />

        <ranking-filter
            v-model="form.filters"
            :language="languageData"
            :show-filters="form.show_all === 0"
            class="mt-3"
            :bonus-types="bonusTypes"
            :game-types="gameTypes"
            :payment-methods="paymentMethods"
            :payout-ratios="payoutRatios"
            :software="software"
            :countries="countries"
            :currencies="currencies"
            :licenses="licenses"
            @filter="runFilters"
        ></ranking-filter>

        <div class="grid grid-cols-2 gap-4 mt-3">
            <div class="bg-white dark:bg-gray-700 rounded-lg p-4 shadow">
                <input-text
                    type="number"
                    label="* Amount to show before load more"
                    v-model.number="form.amount_to_show"
                    :error="
                        errors.amount_to_show ? errors.amount_to_show[0] : null
                    "
                ></input-text>
                <input-text
                    type="number"
                    label="* Number of conversions to update bonus and USPs manually for"
                    v-model.number="form.single_conversion_count"
                    class="mt-3"
                    :error="
                        errors.single_conversion_count
                            ? errors.single_conversion_count[0]
                            : null
                    "
                ></input-text>
                <input-text
                    type="number"
                    label="Total number of conversions to show (leave empty to show all)"
                    v-model.number="form.total_conversions"
                    class="mt-3"
                    :error="
                        errors.total_conversions
                            ? errors.total_conversions[0]
                            : null
                    "
                ></input-text>
                <Btn
                    type="button"
                    @click="submit"
                    v-if="form.selectedCasinos.length"
                    content="Generate Conversions"
                    class="mt-3"
                    :disabled="submitButtonDisabled"
                ></Btn>
            </div>

            <rankings-list
                :filtered="filtered"
                :items="casinos"
                v-model="form.selectedCasinos"
                :max-value="form.single_conversion_count"
            ></rankings-list>
        </div>

        <ranking-conversions
            v-model="form.conversions"
            :language="languageData"
            :highlight="highlight"
            @deleted="deleted"
        ></ranking-conversions>

        <input-checkbox
            name="display_numbers"
            label="Display numbers?"
            class="mt-3"
            v-model="form.display_numbers"
            :error="errors.display_numbers ? errors.display_numbers[0] : null"
        />

        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import crudForm from "../mixins/crudForm";

export default {
    name: "RankingForm",
    mixins: [crudForm],
    props: [
        "languageData",
        "bonusTypes",
        "gameTypes",
        "paymentMethods",
        "software",
        "countries",
        "currencies",
        "licenses",
        "payoutRatios",
    ],
    data() {
        return {
            casinos: [],
            allCasinos: [],
            highlight: [],
            form: {
                editor_note: this.getFormData("editor_note"),
                name: this.getFormData("name"),
                show_all: 0,
                filters: { ...this.data?.filters },
                amount_to_show: this.getFormData("amount_to_show", 10),
                single_conversion_count: this.getFormData(
                    "single_conversion_count",
                    10
                ),
                total_conversions: this.getFormData("total_conversions"),
                conversions: this.getFormData("manual_conversions", []),
                selectedCasinos: [],
                internal_links_to_categories: this.getFormData(
                    "internal_links_to_categories",
                    []
                ),
                display_numbers: this.getFormData("display_numbers", true),
            },
            filtered: false,
            categoryDuplicates: [],
        };
    },
    mounted: function () {
        this.runFilters();
    },
    methods: {
        getFilters: function () {
            return { ...this.data.filters };
        },
        async runFilters() {
            const results = await axios
                .get(
                    route("casinos.filter", {
                        filters: this.form.filters,
                        language: this.form.language,
                    })
                )
                .catch((error) => {
                    this.$notify({
                        group: "formError",
                        title: `Sorry there was a problem running the filters`,
                    });
                });
            this.allCasinos = results.data.data;
            const filteredCasinos = this.allCasinos.filter((casino) => {
                return !this.form.conversions
                    .map((conversion) => conversion.casino_id)
                    .includes(casino.id);
            });
            this.casinos = filteredCasinos;
            this.form.selectedCasinos.length = 0;
            this.filtered = true;
            this.checkConversions();
        },
        checkConversions: function () {
            const conversionsNotMatchedByFilter = this.form.conversions.filter(
                (conversion) => {
                    return !this.allCasinos
                        .map((casino) => casino.id)
                        .includes(conversion.casino_id);
                }
            );
            this.highlight = conversionsNotMatchedByFilter.map((conversion) => {
                return {
                    id: conversion.id,
                    title: "This does not match the filters selected",
                };
            });
        },
        deleted: function () {
            location.reload();
        },
    },
    watch: {
        "form.internal_links_to_categories": function (newValue) {
            const existingValues = [];
            this.categoryDuplicates = newValue
                .map((internalLink) => {
                    if (existingValues.includes(internalLink.document.id)) {
                        return {
                            id: internalLink.id,
                            title:
                                "There is already an internal link which connects to this document",
                        };
                    } else {
                        existingValues.push(internalLink.document.id);
                    }
                })
                .filter((item) => !!item);
        },
        "form.casinoReview": function (newValue) {
            this.form.casino_review_id = newValue.id;
        },
        "form.conversions": function (newValue) {
            const conversions = [...this.form.conversions];
            this.conversions = conversions.map((singleConversion, index) => {
                singleConversion.weight = index;
                return singleConversion;
            });
        },
        "form.show_all": function (newValue) {
            if (newValue === 1) {
                this.form.filters = {};
            }
        },
    },
};
</script>
